import {
  TextField,
  Grid,
  Autocomplete,
  Typography,
  ListSubheader,
} from "@mui/material";
import { useMemo } from "react";
import { matchSorter } from "match-sorter";
import useVademecum from "./hooks/useVademecum";
import _ from "lodash";
import useVademecumStrategy from "./hooks/useStrategy";
import { useHealthCenterRules } from "../../../../../../utils/hooks";

export default function AutocompleteMedicam({
  inputValue,
  setInputValue,
  setMedicineIndex,
  addMedicine,
  disabledAddMedicine,
}) {
  const vademecum = useVademecum();
  const { ListConfig } = useVademecumStrategy();
  const healthCenterMedicinesRules = useHealthCenterRules("medicalDocuments.prescription.medicines");
  const helperText = healthCenterMedicinesRules?.listHelperText || "Escribí al menos 3 caracteres";
  
  const { vademecumMapper, showBrandColumn } = ListConfig;

  const vademecumAddIndex = useMemo(
    () =>
      _(vademecum).map((option, index) => ({
        ...option,
        index,
      }))
      .value(),
    [vademecum]
  );

  const filteredOptions = useMemo(
    () =>
      _(matchSorter(vademecumAddIndex, inputValue, {
        keys: ["marcaComercial", "principioActivo"],
        threshold: matchSorter.rankings.CONTAINS,
      }))
      .thru(vademecumMapper)
      .value(),
    [inputValue, vademecumAddIndex]
  );

  return (
    <Autocomplete
      value={inputValue}
      disabled={disabledAddMedicine}
      onInputChange={(event, newValue) => {
        setInputValue(newValue);
      }}
      open={inputValue.length > 2}
      disableClearable
      freeSolo
      options={filteredOptions}
      onChange={(event, newValue) => {
        setInputValue("");
        addMedicine();
        setMedicineIndex(newValue.index);
      }}
      getOptionLabel={() => inputValue}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            type: "search",
          }}
          placeholder="Buscar medicamento"
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => (
        <Grid
          {...props}
          container
          key={option.index}
          sx={{
            paddingBottom: "0px !important",
            paddingTop: "0px !important",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "lightBlue.main",
            },
          }}
        >
          <Grid
            item
            xs={showBrandColumn ? 6 : 12}
            sx={{
              borderTop: { xs: "none", sm: "0.5px solid grey" },
              borderRight: { xs: "none", sm: "0.5px solid grey" },
            }}
          >
            <Typography
              sx={{ fontSize: { xs: 12, sm: 14 }, p: { xs: 0, sm: 1.5 }, wordWrap: "break-word" }}
            >
              {option.principioActivo}
            </Typography>
          </Grid>

          {showBrandColumn && (
            <Grid
              item
              xs={6}
              sx={{ borderTop: { xs: "none", sm: "0.5px solid grey" } }}
            >
              <Typography
                sx={{ fontSize: { xs: 12, sm: 14 }, p: { xs: 0, sm: 1.5 }, wordWrap: "break-word" }}
              >
                {option.marcaComercial}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
      ListboxComponent={(listboxProps) => (
        <div>
          <ListSubheader>
            <Grid container sx={{ borderBottom: { xs: "0.5px solid grey" } }}>
              <Grid item xs={showBrandColumn ? 6 : 12}>
                <Typography sx={{ fontSize: 12, p: 1.5 }}>
                  Principio Activo
                </Typography>
              </Grid>
              {showBrandColumn && (
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 12, p: 1.5 }}>
                    Marca Comercial
                  </Typography>
                </Grid>
              )}
            </Grid>
          </ListSubheader>
          <ul {...listboxProps} />
        </div>
      )}
    />
  );
}
